import {BrowserModule, HAMMER_GESTURE_CONFIG} from '@angular/platform-browser';
import {Injectable, NgModule, NgModuleFactoryLoader, SystemJsNgModuleLoader} from '@angular/core';
import {ActionReducer, MetaReducer, StoreModule} from '@ngrx/store';
import {AppComponent} from './app.component';
import {GENOMICS_MODULES} from './genomicsModulesLoader';
import {StoreDevtoolsModule} from '@ngrx/store-devtools';
import {reducerProvider, reducerToken} from './core/constants/genomics-state.constant';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {EffectsFeatureModule, EffectsModule} from '@ngrx/effects';
import {environment} from '../environments/environment';
import {ErrorEffects} from '@ispm/shared/store/effects/error.effects';
import {GestureConfig} from '@wuitk/angular/core';
import 'hammerjs';
import {NgxDropzoneModule} from 'ngx-dropzone';
import {ModalLoaderNgjsService} from "@ispm/layout/services/modal-loader-ngjs.service";
import {ApplicationConfigService} from "@ispm/navigation/services/applicationConfig.service";
import {FilterService} from './filter/services/filter.service';
import {WebConfigService} from './layout/services/webConfig.service';
import {FilterActionCreatorService} from './filter/services/filter-action-creator.service';
import {SearchService} from './shared/dynamic-form/services/search.service';
import {AdaptiveFilterService} from "@ispm/shared/services/adaptive-filter.service";
import {UserService} from "@ispm/user/services/user.service";
import {PatientInfoService} from "@ispm/user/services/patientInfo.service";
import {BpmnXmlService} from "@ispm/shared/bpmn/services/bpmn-xml.service";
import {TranslateModule} from '@ngx-translate/core';
import {TranslationHandlerService} from "@ispm/core/services/translation-handler.service";
import {CurationContainerComponent} from './curation/components/curation-common/curation-container.component';
import {AppRoutingModule} from "./app-routing.module";
import { RouteReuseStrategy, DetachedRouteHandle, ActivatedRouteSnapshot } from '@angular/router';
import {NavigationHelperService} from "@ispm/core/services/navigation-helper.service";

export function StoreErrorHandler(reducer: ActionReducer<any>): ActionReducer<any> {
  return function (state, action: any) {
    try {
      return reducer(state, action);
    } catch (e) {
      console.log('Reducer error', e);
      // in case error was thrown we should reload the page since the state might become un-functional.
      window.location.reload();
    }
  };
}

export const metaReducers: MetaReducer<any>[] = [StoreErrorHandler];

export const runtimeChecks = {
  strictStateImmutability: false,
  strictActionImmutability: false,
};

@Injectable({
  providedIn: 'root'
})
export class CustomReuseStrategy implements RouteReuseStrategy {
  private handlers: { [key: string]: DetachedRouteHandle } = {};

  constructor(private navigationHelperService: NavigationHelperService) {
  }

  // Determines if this route (and its subtree) should be detached to be reused later
  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return false; // As of now, we are not storing anything. Making true need to implicit to take care of destroy lifecycle hooks
  }

  // Stores the detached route
  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
    //this.handlers[this.getRouteKey(route)] = handle;
  }

  // Reattaching the subtree sometimes results in stale data - especially in pathways patient dashboard list page
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    return false;
  }

  // Retrieves the previously stored route, causing problems with stale state. need to figure of the components which need this feature
  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle {
    if (!route.routeConfig) return null;
    return null;
  }

  // Comparsion of parameters is must, without this test view routes params change will not result rerendering.
  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    let isPopState = this.navigationHelperService.getNavigationTriggerState();
    return (future.data?.skipRouteReusing === true && isPopState === true) ? false :
      (future.routeConfig === curr.routeConfig &&
        JSON.stringify(future.params) === JSON.stringify(curr.params) &&
        (!future.queryParams?.appname || !curr.queryParams?.appname ||
          JSON.stringify(future.queryParams?.appname) === JSON.stringify(curr.queryParams?.appname)));
  }

  // Helper method to generate a unique key for the route
  private getRouteKey(route: ActivatedRouteSnapshot): string {
    return route.pathFromRoot.map(r => r.url.map(segment => segment.toString()).join('/')).join('/');
  }
}

@NgModule({
  declarations: [
    AppComponent, CurationContainerComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    NgxDropzoneModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslationHandlerService
      },
    }),
    StoreModule.forRoot(reducerToken, {metaReducers, runtimeChecks}),
    EffectsModule.forRoot([ErrorEffects]),
    ...(environment.production ? [] : [
      StoreDevtoolsModule.instrument({
        name: 'Philips ISPM Application',
        maxAge: 50,
        logOnly: true
      }),
    ]),
    ...GENOMICS_MODULES
  ],
  providers: [
    {provide: RouteReuseStrategy, useClass: CustomReuseStrategy},
    {provide: NgModuleFactoryLoader, useClass: SystemJsNgModuleLoader},
    {provide: HAMMER_GESTURE_CONFIG, useClass: GestureConfig},
    reducerProvider,
    EffectsFeatureModule,
    ModalLoaderNgjsService,
    ApplicationConfigService,
    FilterService,
    WebConfigService,
    FilterActionCreatorService,
    SearchService,
    AdaptiveFilterService,
    UserService,
    PatientInfoService,
    BpmnXmlService
  ],
  bootstrap: [AppComponent]
})
export class AppModule {

}


